import {
    Scene,
    MeshBuilder,
    Vector3,
    WebXRDefaultExperience,
    WebXRFeatureName,
} from '@babylonjs/core';
import {
    AdvancedDynamicTexture,
    StackPanel,
    TextBlock,
    Button,
    Slider,
    Control,
    Rectangle,
} from '@babylonjs/gui';

const MUSIC_TRACKS = [
    { label: 'Ambient Cosmos', url: '/assets/audio/ambient_cosmos.wav' },
    { label: 'Chill Synthwave', url: '/assets/audio/chill_synthwave.wav' },
];

export interface VRMenuState {
    locomotion: 'teleport' | 'smooth';
    turning: 'snap' | 'smooth';
    leftHandMove: boolean;
    musicTrack: number;
    volume: number;
    menuVisible: boolean;
}

export function createVRMenu(
    scene: Scene,
    xr: WebXRDefaultExperience,
    onStateChange: (state: VRMenuState) => void,
) {
    const state: VRMenuState = {
        locomotion: 'teleport',
        turning: 'snap',
        leftHandMove: true, // default: left stick = move, right stick = turn
        musicTrack: 0,
        volume: 0.5,
        menuVisible: false,
    };

    // --- 3D Panel Mesh ---
    const menuPlane = MeshBuilder.CreatePlane('vrMenuPlane', { width: 1.2, height: 2.2 }, scene);
    menuPlane.position = new Vector3(0, 1.5, 2);
    menuPlane.isPickable = true;
    menuPlane.isVisible = false;

    // --- GUI Texture on the panel ---
    const advTexture = AdvancedDynamicTexture.CreateForMesh(menuPlane, 600, 1100);

    // Background
    const bg = new Rectangle('bg');
    bg.width = '100%';
    bg.height = '100%';
    bg.cornerRadius = 20;
    bg.color = '#00e6ff';
    bg.thickness = 3;
    bg.background = 'rgba(10, 10, 30, 0.92)';
    advTexture.addControl(bg);

    // Main stack
    const mainStack = new StackPanel('mainStack');
    mainStack.width = '90%';
    mainStack.isVertical = true;
    mainStack.spacing = 8;
    mainStack.paddingTopInPixels = 20;
    bg.addControl(mainStack);

    // --- Title ---
    const title = new TextBlock('title', '⚙️ VR Settings');
    title.color = '#00e6ff';
    title.fontSize = 42;
    title.height = '55px';
    title.fontFamily = 'Arial, sans-serif';
    title.fontWeight = 'bold';
    mainStack.addControl(title);

    addSeparator(mainStack);

    // --- Locomotion Section ---
    const locoLabel = makeLabel('🚀 Locomotion');
    mainStack.addControl(locoLabel);

    const locoTeleBtn = makeToggleButton('locoTele', 'Teleport', true);
    const locoSmoothBtn = makeToggleButton('locoSmooth', 'Smooth Walk', false);

    const locoRow = new StackPanel('locoRow');
    locoRow.isVertical = false;
    locoRow.height = '50px';
    locoRow.spacing = 10;
    locoRow.addControl(locoTeleBtn);
    locoRow.addControl(locoSmoothBtn);
    mainStack.addControl(locoRow);

    locoTeleBtn.onPointerUpObservable.add(() => {
        state.locomotion = 'teleport';
        setToggle(locoTeleBtn, true);
        setToggle(locoSmoothBtn, false);
        applyLocomotion(scene, xr, state);
        onStateChange({ ...state });
    });
    locoSmoothBtn.onPointerUpObservable.add(() => {
        state.locomotion = 'smooth';
        setToggle(locoTeleBtn, false);
        setToggle(locoSmoothBtn, true);
        applyLocomotion(scene, xr, state);
        onStateChange({ ...state });
    });

    addSeparator(mainStack);

    // --- Turning Section ---
    const turnLabel = makeLabel('🔄 Turning');
    mainStack.addControl(turnLabel);

    const snapBtn = makeToggleButton('snapBtn', 'Snap', true);
    const smoothTurnBtn = makeToggleButton('smoothTurnBtn', 'Smooth', false);

    const turnRow = new StackPanel('turnRow');
    turnRow.isVertical = false;
    turnRow.height = '50px';
    turnRow.spacing = 10;
    turnRow.addControl(snapBtn);
    turnRow.addControl(smoothTurnBtn);
    mainStack.addControl(turnRow);

    snapBtn.onPointerUpObservable.add(() => {
        state.turning = 'snap';
        setToggle(snapBtn, true);
        setToggle(smoothTurnBtn, false);
        onStateChange({ ...state });
    });
    smoothTurnBtn.onPointerUpObservable.add(() => {
        state.turning = 'smooth';
        setToggle(snapBtn, false);
        setToggle(smoothTurnBtn, true);
        onStateChange({ ...state });
    });

    addSeparator(mainStack);

    // --- Hand Assignment Section ---
    const handLabel = makeLabel('🖐️ Controller Layout');
    mainStack.addControl(handLabel);

    const leftMoveBtn = makeToggleButton('leftMove', 'L=Move R=Turn', true);
    const rightMoveBtn = makeToggleButton('rightMove', 'L=Turn R=Move', false);

    const handRow = new StackPanel('handRow');
    handRow.isVertical = false;
    handRow.height = '50px';
    handRow.spacing = 10;
    handRow.addControl(leftMoveBtn);
    handRow.addControl(rightMoveBtn);
    mainStack.addControl(handRow);

    leftMoveBtn.onPointerUpObservable.add(() => {
        state.leftHandMove = true;
        setToggle(leftMoveBtn, true);
        setToggle(rightMoveBtn, false);
        applyLocomotion(scene, xr, state);
        onStateChange({ ...state });
    });
    rightMoveBtn.onPointerUpObservable.add(() => {
        state.leftHandMove = false;
        setToggle(leftMoveBtn, false);
        setToggle(rightMoveBtn, true);
        applyLocomotion(scene, xr, state);
        onStateChange({ ...state });
    });

    addSeparator(mainStack);

    // --- Music Section ---
    const musicLabel = makeLabel('🎵 Music');
    mainStack.addControl(musicLabel);

    const trackBtns: Button[] = [];
    MUSIC_TRACKS.forEach((track, idx) => {
        const btn = makeToggleButton(`track${idx}`, track.label, idx === 0);
        trackBtns.push(btn);
        btn.onPointerUpObservable.add(() => {
            state.musicTrack = idx;
            trackBtns.forEach((b, i) => setToggle(b, i === idx));
            switchTrack(idx);
            onStateChange({ ...state });
        });
    });

    const trackRow = new StackPanel('trackRow');
    trackRow.isVertical = false;
    trackRow.height = '50px';
    trackRow.spacing = 10;
    trackBtns.forEach((b) => trackRow.addControl(b));
    mainStack.addControl(trackRow);

    // Volume slider
    const volRow = new StackPanel('volRow');
    volRow.isVertical = false;
    volRow.height = '50px';
    volRow.spacing = 10;

    const volLabel = new TextBlock('volLabel', '🔊 Vol');
    volLabel.color = '#aaaacc';
    volLabel.fontSize = 22;
    volLabel.width = '80px';
    volLabel.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    volRow.addControl(volLabel);

    const volSlider = new Slider('volSlider');
    volSlider.minimum = 0;
    volSlider.maximum = 1;
    volSlider.value = state.volume;
    volSlider.height = '30px';
    volSlider.width = '320px';
    volSlider.color = '#00e6ff';
    volSlider.background = '#222244';
    volSlider.thumbColor = '#ffffff';
    volSlider.isThumbCircle = true;
    volSlider.onValueChangedObservable.add((val: number) => {
        state.volume = val;
        setVolume(val);
        onStateChange({ ...state });
    });
    volRow.addControl(volSlider);
    mainStack.addControl(volRow);

    addSeparator(mainStack);

    // --- Close button ---
    const closeBtn = Button.CreateSimpleButton('closeBtn', '✖ Close');
    closeBtn.width = '200px';
    closeBtn.height = '45px';
    closeBtn.color = '#ff4466';
    closeBtn.fontSize = 24;
    closeBtn.cornerRadius = 12;
    closeBtn.background = 'rgba(255,68,102,0.15)';
    closeBtn.thickness = 2;
    closeBtn.onPointerUpObservable.add(() => {
        toggleMenu(false);
    });
    mainStack.addControl(closeBtn);

    // --- Audio Setup (native HTML5 Audio for reliability) ---
    let audioEl: HTMLAudioElement | null = null;

    function switchTrack(idx: number) {
        if (audioEl) {
            audioEl.pause();
            audioEl.removeAttribute('src');
            audioEl.load();
        }
        if (idx >= 0 && idx < MUSIC_TRACKS.length) {
            audioEl = new Audio(MUSIC_TRACKS[idx].url);
            audioEl.loop = true;
            audioEl.volume = state.volume;
            const playPromise = audioEl.play();
            if (playPromise) {
                playPromise.catch((e) => {
                    console.warn('Audio autoplay blocked, will retry on interaction:', e);
                });
            }
            console.log('Now playing:', MUSIC_TRACKS[idx].label);
        }
    }

    function setVolume(vol: number) {
        if (audioEl) {
            audioEl.volume = vol;
        }
    }

    // Defer audio start until first user interaction
    let audioStarted = false;
    function startAudioOnInteraction() {
        if (audioStarted) return;
        audioStarted = true;
        switchTrack(0);
    }

    // Listen for first pointer event to unlock audio
    const audioObserver = scene.onPointerObservable.add(() => {
        startAudioOnInteraction();
        scene.onPointerObservable.remove(audioObserver);
    });

    // Also start audio when entering XR (click on Enter VR button is a user gesture)
    if (xr.baseExperience) {
        xr.baseExperience.onStateChangedObservable.add((xrState: any) => {
            if (xrState === 2) { // IN_XR
                startAudioOnInteraction();
            }
        });
    }

    // --- Menu Toggle ---
    function toggleMenu(forceState?: boolean) {
        state.menuVisible = forceState !== undefined ? forceState : !state.menuVisible;
        menuPlane.isVisible = state.menuVisible;

        if (state.menuVisible) {
            // Position the menu in front of the active camera
            const cam = scene.activeCamera;
            if (cam) {
                const forward = cam.getForwardRay().direction.scale(2);
                menuPlane.position = cam.position.add(forward);
                menuPlane.position.y = cam.position.y;
                menuPlane.lookAt(cam.position);
                // Flip it since lookAt faces away
                menuPlane.rotation.y += Math.PI;
            }
        }
        onStateChange({ ...state });
    }

    // --- Controller Menu Button Binding ---
    if (xr.input) {
        xr.input.onControllerAddedObservable.add((controller: any) => {
            controller.onMotionControllerInitObservable.add((motionController: any) => {
                // Check for the menu button (usually on left controller)
                const menuComponent = motionController.getComponent('menu') ||
                    motionController.getComponent('xr-standard-thumbrest') ||
                    motionController.getComponent('a-button') ||
                    motionController.getComponent('x-button');

                if (menuComponent) {
                    menuComponent.onButtonStateChangedObservable.add(() => {
                        if (menuComponent.pressed) {
                            toggleMenu();
                        }
                    });
                    console.log('VR Menu bound to controller button:', menuComponent.id);
                }

                // Also try the "thumbstick" press or Y button as fallback
                const yButton = motionController.getComponent('y-button');
                if (yButton) {
                    yButton.onButtonStateChangedObservable.add(() => {
                        if (yButton.pressed) {
                            toggleMenu();
                        }
                    });
                    console.log('VR Menu also bound to Y button');
                }
            });
        });
    }

    // --- Apply Locomotion Mode ---
    function applyLocomotion(sc: Scene, xrInst: WebXRDefaultExperience, st: VRMenuState) {
        if (!xrInst.baseExperience) return;
        const fm = xrInst.baseExperience.featuresManager;
        const groundMesh = sc.getMeshByName('ground');

        if (st.locomotion === 'teleport') {
            // Disable movement, enable teleportation
            try { fm.disableFeature(WebXRFeatureName.MOVEMENT); } catch {}
            try {
                fm.enableFeature(WebXRFeatureName.TELEPORTATION, 'stable', {
                    xrInput: xrInst.input,
                    floorMeshes: groundMesh ? [groundMesh] : [],
                });
            } catch {}
            console.log('Switched to teleportation');
        } else {
            // Disable teleportation, enable smooth movement
            try { fm.disableFeature(WebXRFeatureName.TELEPORTATION); } catch {}
            try {
                fm.enableFeature(WebXRFeatureName.MOVEMENT, 'stable', {
                    xrInput: xrInst.input,
                    movementSpeed: 0.25,
                    rotationSpeed: 0.4,
                    movementOrientationFollowsViewerPose: true,
                    customRegistrationConfigurations: [
                        {
                            componentSelectionPredicate: (comp: any) => comp.type === 'thumbstick',
                            handlers: {
                                onButtonChangedObservable: [
                                    {
                                        handedness: st.leftHandMove ? 'left' : 'right',
                                        allowMovement: true,
                                    },
                                    {
                                        handedness: st.leftHandMove ? 'right' : 'left',
                                        allowRotation: true,
                                    }
                                ]
                            }
                        }
                    ],
                });
            } catch (e) {
                console.warn('Movement feature config warning:', e);
            }
            console.log(`Switched to smooth movement (speed: 0.25, ${st.leftHandMove ? 'L=Move R=Turn' : 'L=Turn R=Move'})`);
        }
    }

    // Mute toggle for desktop button
    let muted = false;
    function toggleMute(): boolean {
        muted = !muted;
        if (audioEl) {
            if (muted) {
                audioEl.pause();
            } else {
                audioEl.play().catch(() => {});
            }
        }
        return muted;
    }

    // Public API
    return {
        toggleMenu,
        toggleMute,
        isMuted: () => muted,
        startAudio: startAudioOnInteraction,
        getState: () => ({ ...state }),
        dispose: () => {
            if (audioEl) {
                audioEl.pause();
                audioEl.removeAttribute('src');
                audioEl.load();
                audioEl = null;
            }
            menuPlane.dispose();
            advTexture.dispose();
        },
    };
}

// --- GUI Helpers ---

function makeLabel(text: string): TextBlock {
    const tb = new TextBlock();
    tb.text = text;
    tb.color = '#ccccee';
    tb.fontSize = 26;
    tb.height = '35px';
    tb.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    tb.paddingLeftInPixels = 10;
    return tb;
}

function makeToggleButton(name: string, label: string, active: boolean): Button {
    const btn = Button.CreateSimpleButton(name, label);
    btn.width = '200px';
    btn.height = '45px';
    btn.fontSize = 20;
    btn.cornerRadius = 10;
    btn.thickness = 2;
    setToggle(btn, active);
    return btn;
}

function setToggle(btn: Button, active: boolean) {
    if (active) {
        btn.color = '#00e6ff';
        btn.background = 'rgba(0, 230, 255, 0.2)';
    } else {
        btn.color = '#666688';
        btn.background = 'rgba(50, 50, 80, 0.3)';
    }
}

function addSeparator(panel: StackPanel) {
    const sep = new Rectangle('sep_' + Math.random().toString(36).slice(2));
    sep.width = '100%';
    sep.height = '2px';
    sep.background = 'rgba(0, 230, 255, 0.2)';
    sep.thickness = 0;
    panel.addControl(sep);
}
